import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import Box from '@mui/material/Box';
import {
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  AppFormikTextArea,
  AppFormikTextField,
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
} from 'shared/components/AppComponents';
import { Formik } from 'formik';
import { AuditGateScheme } from 'validators/AuditGateScheme';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { PoliciesService } from 'services';
import useSize from 'shared/components/Hooks';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface AuditModalProps {
  open: boolean;
  selectedGateId?: string | null;
  onClose: () => void;
  onSubmit: (values: any) => void;
}

interface ApprovalGate {
  _id: string;
  gate_name: string;
  required_artifacts: string;
  reviewers: string;
  sort_order: number;
  organizationId: string;
  created_by: string;
  updated_by: string;
  deleted_by: string | null;
  deleted_at: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

const AuditModal: React.FC<AuditModalProps> = forwardRef(
  // eslint-disable-next-line no-empty-pattern
  ({ open, selectedGateId, onClose, onSubmit }, ref): React.ReactElement => {
    useImperativeHandle(ref, () => ({}), []);
    const { isDesktop } = useSize();

    const [selectedGate, setSelectedGate] = useState<ApprovalGate | null>(null);
    const [detailsLoading, setDetailsLoading] = useState(false);

    const getInitialValues = (): any => {
      if (selectedGate) {
        return {
          gatename: selectedGate.gate_name || '',
          gateartifact: selectedGate.required_artifacts || '',
          reviewers: selectedGate.reviewers || '',
        };
      }
      return {
        gatename: '',
        gateartifact: '',
        reviewers: '',
      };
    };

    const getApprovalById = (id: string): void => {
      setDetailsLoading(true);
      PoliciesService.getApproval(id)
        .then((response: any) => {
          setSelectedGate(response.data.data);
        })
        .catch((error) => {
          console.error('Failed to fetch approval gate:', error);
          setSelectedGate(null);
        })
        .finally(() => {
          setDetailsLoading(false);
        });
    };

    useEffect(() => {
      if (selectedGateId) getApprovalById(selectedGateId);
    }, [selectedGateId]);

    useEffect(() => {
      if (open && !selectedGateId) {
        setSelectedGate(null);
      }
    }, [open, selectedGateId]);

    return (
      // <Box>
      <Dialog
        open={open}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown
        onClose={onClose}
        aria-labelledby="parent-modal-title"
        aria-describedby="parent-modal-description"
      >
        {detailsLoading ? (
          <Box className="empty-content">
            <CircularProgress />
          </Box>
        ) : (
          <Formik
            initialValues={getInitialValues()}
            enableReinitialize
            validateOnMount
            validationSchema={AuditGateScheme}
            onSubmit={(values, { setSubmitting }) => {
              onSubmit(values);
              setSubmitting(false);
            }}
          >
            {({ handleSubmit }) => (
              <Box className="container-audit-modal">
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    variant={isDesktop ? 'h2' : 'h4'}
                    id="parent-modal-title"
                    className={isDesktop ? '' : 'fw-7 f20'}
                  >
                    {selectedGate
                      ? 'Edit Approval Gate'
                      : 'Create Approval Gate'}
                  </Typography>
                  <IconButton onClick={onClose} className="mr-2">
                    <HighlightOffIcon />
                  </IconButton>
                </Stack>

                <Box className="container-audit-detail">
                  <AppFormikTextField
                    name="gatename"
                    placeholder="e.g., Security, Review, Data Privacy Assessment"
                    label="Gate Name *"
                    outerLabel
                  />
                  <AppFormikTextArea
                    name="gateartifact"
                    placeholder="List the documents, report, or deliverables required for this gate..."
                    label="Required Artifacts"
                    outerLabel
                  />
                  <AppFormikTextField
                    name="reviewers"
                    placeholder="e.g., Security Team, Data Protection Officer, Legal"
                    label=" Reviewers"
                    outerLabel
                  />
                </Box>

                <Stack
                  className="action-to-complete-fix-packet"
                  direction="row-reverse"
                >
                  <AppPrimaryButton
                    title={selectedGate ? 'Update Gate' : 'Create Gate'}
                    onClick={handleSubmit}
                  />
                  <AppPrimaryButtonOutlined title="Cancel" onClick={onClose} />
                </Stack>
              </Box>
            )}
          </Formik>
        )}
      </Dialog>
      // </Box>
    );
  }
);

export default AuditModal;
